import {
  Attachment,
  Message,
  MessageRole,
  SearchSource,
  StreamStatusEntry,
} from 'features/chat/types/chat';
import {
  ChatFileMetadata,
  ChatMessage,
} from 'features/chat/agent-chat-module/types/chat-store.types';
import { mergeStreamStatusEntriesFromGenerations, resolveStreamActivityEntries } from 'features/chat/utils/stream-status-entries';
import { isValidUrl, placeHrefInContent } from 'utils/chat-utilities';

const normalizeAttachmentExtension = (extension: string) => extension.replace(/^\./, '');

export const toAgentAttachments = (files?: ChatFileMetadata[]): Attachment[] | undefined => {
  if (!files?.length) return undefined;
  return files.map((file) => ({
    fileId: file.fileId,
    fileName: file.fileName,
    extension: normalizeAttachmentExtension(file.extension),
    sender: MessageRole.USER,
    size: file.fileSize,
  }));
};

export const mapMainStoreMessagesToAgentChat = (
  messages: Message[],
  streamingAssistantMessageId?: string | null,
  isCurrentResponseGenerating?: boolean,
): ChatMessage[] => {
  const result: ChatMessage[] = [];

  const normalizeSearchSources = (sources?: SearchSource[]): SearchSource[] | undefined => {
    if (!sources?.length) return undefined;
    const filtered = sources.filter(
      (source) => source.metadata?.url && isValidUrl(source.metadata.url),
    );
    return filtered.length > 0 ? filtered : undefined;
  };

  for (const message of messages) {
    if (message.role === 'user') {
      const generation = message.generations[0];
      if (!generation) continue;

      result.push({
        type: 'user',
        message: generation.content,
        streaming: false,
        timestamp: generation.showMessageTime || '',
        files: generation.attachments?.map((attachment) => ({
          fileId: attachment.fileId,
          fileName: attachment.fileName,
          fileUrl: '',
          extension: attachment.extension,
          fileSize: attachment.size,
        })),
      });
      continue;
    }

    if (
      message.role === MessageRole.ASSISTANT ||
      message.role === MessageRole.ASSISTANT_CHAT
    ) {
      const generationIndex = message.activeGenerationIndex ?? 0;
      const generation = message.generations[generationIndex] ?? message.generations[0];
      if (!generation) continue;

      const matchesStreamingTarget =
        streamingAssistantMessageId &&
        (message.recordId === streamingAssistantMessageId ||
          message.generations.some((g) => g.id === streamingAssistantMessageId));

      const isStreaming =
        Boolean(isCurrentResponseGenerating && matchesStreamingTarget);

      const searchSources = normalizeSearchSources(generation.searchSources);
      const messageText = searchSources
        ? placeHrefInContent(generation.content ?? '', searchSources)
        : (generation.content ?? '');

      const streamActivityEntries = resolveStreamActivityEntries(generation);

      result.push({
        type: 'bot',
        message: messageText,
        streaming: Boolean(isStreaming),
        timestamp: generation.showMessageTime || '',
        messageId: generation.id,
        userRecordId: message.userRecordId,
        feedback: generation.feedback,
        isAnalyzing: generation.isAnalyzing,
        isAnalyzed: generation.isAnalyzed,
        codeExecutionSteps: generation.codeExecutionSteps,
        thinkContent: generation.thinkContent,
        attachments: generation.attachments?.filter(
          (attachment) => attachment.sender === MessageRole.ASSISTANT,
        ),
        generatedImages: generation.generatedImages?.filter(
          (attachment) => attachment.sender === MessageRole.ASSISTANT,
        ),
        ...(searchSources && { searchSources }),
        ...(streamActivityEntries.length > 0 && { streamActivityEntries }),
        ...(generation.a2uiSurfaces?.length && { a2uiSurfaces: generation.a2uiSurfaces }),
        ...(generation.streamBlocks?.length && { streamBlocks: generation.streamBlocks }),
      });
    }
  }

  return result;
};

/** Reads tool/skill timeline from the in-flight assistant generation (ids may differ before reconcile). */
export function getActiveStreamStatusEntries(
  messages: Message[],
  streamingAssistantMessageId?: string | null,
  isCurrentResponseGenerating?: boolean,
): StreamStatusEntry[] | undefined {
  if (!isCurrentResponseGenerating) return undefined;

  let bestEntries: StreamStatusEntry[] | undefined;
  let bestScore = -1;

  for (const message of messages) {
    if (message.role !== MessageRole.ASSISTANT) continue;

    for (let genIndex = 0; genIndex < message.generations.length; genIndex++) {
      const generation = message.generations[genIndex];
      const merged = mergeStreamStatusEntriesFromGenerations(message.generations, genIndex);
      const entries = merged.streamStatusEntries;
      if (!entries?.length) continue;

      let score = entries.length;
      if (
        streamingAssistantMessageId &&
        (message.recordId === streamingAssistantMessageId ||
          generation.id === streamingAssistantMessageId)
      ) {
        score += 100;
      }
      if (generation.isThisGenerationOnGoing) score += 50;
      if (!generation?.content?.trim()) score += 10;

      if (score > bestScore) {
        bestScore = score;
        bestEntries = entries;
      }
    }
  }

  return bestEntries;
}